import { useCallback, useEffect, useRef, useState, useMemo } from 'react'
import { getFeedsForWallSkipLimit } from '../../../../../services/feed/feed.services'
import CardFeed from '../../../Feed/Card/Card'
import CardFeedLoading from '../../../Feed/Card/Loading'
import FeedDialog from '../../../Feed/Dialog/Dialog'
import Box from '@mui/material/Box'
import Typography from '@mui/material/Typography'
import Skeleton from '@mui/material/Skeleton'
import Chip from '@mui/material/Chip'
import ButtonStartIcon from '../../../../button/ButtonStartIcon'
import { Paper } from '@mui/material'
import useFetchData from '../../../../../hooks/useFetchData'

const LIMIT = 10
const SCROLL_THRESHOLD_PX = 200

const Feeds = ({ userData, featureFlags, permissions }) => {
  const { _id: userId, roles = [], yearLevel = [], children = [], teacherLevel = [] } = userData || {}
  // Start in the loading state so the home feed shows the skeleton straight away instead of a blank
  // frame → skeleton → content. Avoids the "flash of nothing" on login.
  const [isLoading, setIsLoading] = useState(true)
  const [isLoadingMore, setIsLoadingMore] = useState(false)
  const [data, setData] = useState([])
  const [skip, setSkip] = useState(0)
  const [hasMore, setHasMore] = useState(true)
  const [modal, setModal] = useState(null)
  const { isLoading: isLoadingRoles, data: dataRoles = [] } = useFetchData('roles/standard')

  const [selectedFilter, setSelectedFilter] = useState('all')

  // TODO: Harcoded filter options, move to backend later
  const filterOptions = [
    {
      id: 'all',
      value: 'all',
      label: 'All',
      icon: '📋',
      color: '#6b7280',
      backgroundColor: '#f9fafb',
      borderColor: '#e5e7eb'
    },
    {
      id: 'announcement',
      value: 'announcement',
      label: 'Announcement',
      icon: '📢',
      color: '#1d4ed8',
      backgroundColor: '#eff6ff',
      borderColor: '#60a5fa'
    },
    {
      id: 'task',
      value: 'task',
      label: 'Task',
      icon: '📝',
      color: '#be123c',
      backgroundColor: '#fff1f2',
      borderColor: '#fb7185'
    },
    {
      id: 'document',
      value: 'document',
      label: 'Document',
      icon: '📄',
      color: '#047857',
      backgroundColor: '#ecfdf5',
      borderColor: '#34d399'
    },
    {
      id: 'achievement',
      value: 'achievement',
      label: 'Achievement',
      icon: '🏆',
      color: '#b45309',
      backgroundColor: '#fffbeb',
      borderColor: '#fbbf24'
    }
  ]

  const handleFilterChange = (filterValue) => {
    setSelectedFilter(filterValue)
  }

  const containerRef = useRef(null)
  const yearLevelRoles = ['Teacher', 'Student', 'Parent']
  const hasYearLevelRole = roles.some((role) =>
    yearLevelRoles.some((yrRole) => role?.id?.toLowerCase() === yrRole.toLowerCase())
  )

  // Filter data based on selectedFilter
  const filteredData = useMemo(() => {
    if (!selectedFilter || selectedFilter === 'all') {
      return data
    }

    return data.filter((feed) => {
      const postType = feed?.postType?.value
      return postType === selectedFilter
    })
  }, [data, selectedFilter])

  const loadFeeds = useCallback(
    async (reset = false) => {
      try {
        if (reset) {
          setIsLoading(true)
          setSkip(0)
        } else {
          setIsLoadingMore(true)
        }

        // const getYearLevelsForRoles = () => {
        //   if (!hasYearLevelRole) return []

        //   const isParent = roles.some((role) => role?.id?.toLowerCase() === 'parent')
        //   const isTeacher = roles.some((role) => role?.id?.toLowerCase() === 'teacher')
        //   const isStudent = roles.some((role) => role?.id?.toLowerCase() === 'student')

        //   const levels = isParent
        //     ? Array.from(
        //         new Map(
        //           children?.flatMap((child) => child?.yearLevel || []).map((level) => [level?.name, level])
        //         ).values()
        //       )
        //     : isTeacher
        //       ? teacherLevel
        //       : isStudent
        //         ? yearLevel
        //         : []

        //   return levels
        // }

        // const yearLevels = getYearLevelsForRoles()
        const currentSkip = reset ? 0 : skip
        const response = await getFeedsForWallSkipLimit(
          currentSkip,
          LIMIT,
          selectedFilter !== 'all'
            ? {
                postType: selectedFilter
                // ...(hasYearLevelRole ? { yearLevels: yearLevels?.map((yr) => yr?.name || yr?.id) } : {})
              }
            : {}
          // : { ...(hasYearLevelRole ? { yearLevels: yearLevels?.map((yr) => yr?.name || yr?.id) } : {}) }
        )
        if (response?.error) {
          console.error(response.error)
          return
        }
        const items = response?.data || []

        if (reset) {
          setData(items)
          setSkip(items.length)
        } else {
          setData((prev) => [...prev, ...items])
          setSkip((prev) => prev + items.length)
        }

        setHasMore(items.length === LIMIT)
      } catch (err) {
        console.error(err)
      } finally {
        setIsLoading(false)
        setIsLoadingMore(false)
      }
    },
    [skip, selectedFilter]
  )

  useEffect(() => {
    if (selectedFilter) {
      loadFeeds(true)
    }
  }, [selectedFilter])

  const handleDialogResult = (result) => {
    if (!result) return
    const { changed, action, feed: item } = result
    if (!changed) return

    setData((prev) => {
      if (!action) return prev

      switch (action) {
        case 'create':
          setSkip((s) => s + 1)
          return [item, ...prev]
        case 'update':
        case 'edit':
          return prev.map((f) => (f._id === item._id ? item : f))
        case 'delete':
          setSkip((s) => Math.max(0, s - 1))
          return prev.filter((f) => f._id !== item._id)
        case 'archive':
          return prev.map((f) => (f._id === item._id ? item : f))
        default:
          return prev
      }
    })
  }

  const handleScroll = (e) => {
    const el = e.target
    if (isLoading || isLoadingMore || !hasMore) return
    if (el.scrollHeight - el.scrollTop - el.clientHeight < SCROLL_THRESHOLD_PX) {
      loadFeeds(false)
    }
  }

  // Stable empty state (shown once loading finishes with no posts). Having a real message — instead
  // of rendering nothing for the 'all' filter — is what stops the skeleton "falling to blank".
  const NoPostsMessage = () => (
    <div className='text-center py-8'>
      <div className='text-6xl mb-4'>📢</div>
      <h3 className='text-lg font-semibold text-gray-600 mb-2'>No posts yet</h3>
      <p className='text-gray-500'>
        {selectedFilter && selectedFilter !== 'all'
          ? `No ${selectedFilter} posts found. Try selecting a different filter.`
          : 'There are no posts to show right now.'}
      </p>
    </div>
  )

  return (
    <>
      {/* Header Section */}
      <Paper sx={{ py: 2, px: 3 }} elevation={1}>
        {/* Create Feed Section */}
        <Box className='flex justify-end col-span-2'>
          {featureFlags?.showFeed && permissions?.created && (
            <ButtonStartIcon
              id='create-feed-button'
              onClick={() =>
                setModal({
                  type: 'create',
                  data: {
                    pusblishedAt: new Date().toISOString(),
                    expireAt: new Date(new Date().setDate(new Date().getDate() + 30)).toISOString(),
                    minDays: 30,
                    title: '',
                    content: '',
                    viewRoles: isLoadingRoles ? [] : dataRoles
                  }
                })
              }
              color='primary'
              variant='contained'
              startIcon={['fal', 'plus']}
              style={{
                // borderRadius: '12px',
                textTransform: 'capitalize',
                fontWeight: 600,
                fontSize: '1rem',
                px: 3,
                py: 1.5,
                boxShadow: '0 4px 12px rgba(59, 130, 246, 0.25)',
                '&:hover': {
                  boxShadow: '0 6px 20px rgba(59, 130, 246, 0.35)',
                  transform: 'translateY(-1px)'
                },
                transition: 'all 0.2s ease-in-out'
              }}
            >
              Create Feed
            </ButtonStartIcon>
          )}
        </Box>
        {/* Filter Chips */}
        <Box
          sx={{
            display: 'flex',
            gap: 1.5,
            flexWrap: 'wrap',
            alignItems: 'center'
          }}
        >
          {filterOptions?.map((filter) => (
            <Chip
              key={filter.id}
              label={
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <span style={{ fontSize: '14px' }}>{filter.icon}</span>
                  <span style={{ fontWeight: 500 }}>{filter.label}</span>
                </Box>
              }
              onClick={() => handleFilterChange(filter.value)}
              variant={selectedFilter === filter.value ? 'filled' : 'outlined'}
              sx={{
                backgroundColor: selectedFilter === filter.value ? filter.backgroundColor : 'transparent',
                color: selectedFilter === filter.value ? filter.color : '#6b7280',
                borderColor: selectedFilter === filter.value ? filter.borderColor : '#d1d5db',
                borderRadius: '20px',
                height: '36px',
                fontWeight: 600,
                fontSize: '0.875rem',
                cursor: 'pointer',
                transition: 'all 0.2s ease-in-out',
                '&:hover': {
                  backgroundColor: filter.backgroundColor,
                  color: filter.color,
                  borderColor: filter.borderColor,
                  transform: 'translateY(-1px)',
                  boxShadow: `0 4px 12px ${filter.color}20`
                },
                '& .MuiChip-label': {
                  padding: '0 12px'
                }
              }}
            />
          ))}
        </Box>
      </Paper>
      <div
        ref={containerRef}
        onScroll={handleScroll}
        className='overflow-y-auto space-y-4'
        style={{ maxHeight: '100%' }}
      >
        {isLoading ? (
          Array.from({ length: 3 }).map((_, index) => <CardFeedLoading key={`feed-loading-${index}`} />)
        ) : filteredData.length > 0 ? (
          filteredData.map((feed, i) => (
            <CardFeed
              key={`Feed-${feed._id}-${i}`}
              feed={feed}
              isPreview={feed?.updatedBy?.userId !== userId || feed?.createdBy?.userId !== userId}
              onModal={(type, data) => setModal({ type, data })}
            />
          ))
        ) : (
          <NoPostsMessage />
        )}

        {isLoadingMore && <CardFeedLoading key='feed-loading-more' />}

        {modal && (
          <FeedDialog
            show={!!modal}
            type={modal.type}
            data={modal.data}
            onClose={(result) => {
              handleDialogResult(result)
              setModal(null)
            }}
          />
        )}
      </div>
    </>
  )
}

export default Feeds
